/**
 * Sends planned files to vfs-service: packed into a zip in the browser (fflate; already-compressed formats stored,
 * the rest deflated) and posted to POST /upload, extracted at the target with the chosen conflict policy. One archive
 * (one commit) unless the files exceed what one archive may carry. Bytes go as they are: binaries arrive identical.
 *
 * Progress: each file goes queued → packing → packed → sent (the server's status: created | overwritten | renamed |
 * skipped) or failed / cancelled. Cancelling stops packing at once and aborts the request in flight; an archive the
 * server already received may still have been saved, which the final report says.
 */
import { Zip, ZipDeflate, ZipPassThrough } from 'fflate';
import * as vfsClient from '../vfsClient';
import { batchFiles, batchOptions, compressionLevel, joinPath, SERVER_LIMITS } from './uploadPlan';

/** The bytes of a File/Blob (FileReader where `arrayBuffer` is missing, e.g. jsdom). */
export function readBytes(blob) {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer().then((buffer) => new Uint8Array(buffer));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

const nextTick = () => new Promise((resolve) => setTimeout(resolve, 0));

class Cancelled extends Error {
  constructor() {
    super('Cancelled');
    this.name = 'Cancelled';
  }
}

/**
 * Packs `files` ([{relPath, file}]) into one zip Blob. `onFile(index)` after each file; stops when `signal` aborts.
 * Also returns the bytes read, keyed by relPath (the local cache stores them without reading the files again).
 */
export async function packZip(files, { signal, onFile } = {}) {
  const chunks = [];
  let failure = null;
  let settle;
  const done = new Promise((resolve, reject) => { settle = { resolve, reject }; });
  done.catch(() => null); // a failure is rethrown below, where it is awaited
  const zip = new Zip((error, data, final) => {
    if (error) { failure = error; settle.reject(error); return; }
    chunks.push(data);
    if (final) { settle.resolve(); }
  });
  const bytesByPath = new Map();
  for (let index = 0; index < files.length; index += 1) {
    if (signal?.aborted) { zip.terminate(); throw new Cancelled(); }
    if (failure) { throw failure; }
    const { relPath, file } = files[index];
    // eslint-disable-next-line no-await-in-loop
    const bytes = await readBytes(file);
    bytesByPath.set(relPath, bytes);
    const level = compressionLevel(relPath);
    const entry = level === 0 ? new ZipPassThrough(relPath) : new ZipDeflate(relPath, { level });
    zip.add(entry);
    entry.push(bytes, true);
    onFile?.(index);
    // Let the page breathe between files (progress repaints, Cancel stays clickable).
    // eslint-disable-next-line no-await-in-loop
    await nextTick();
  }
  zip.end();
  await done;
  return { archive: new Blob(chunks, { type: 'application/zip' }), bytesByPath };
}

/**
 * @param {object} options
 * @param {string} options.projectId
 * @param {string} options.vfsTarget project-relative folder the files go into ('' = project root)
 * @param {Array<{relPath: string, destPath: string, file: File, size: number}>} options.files destPath: tree-relative
 * @param {'overwrite'|'skip'|'rename'} options.conflict
 * @param {AbortSignal} [options.signal]
 * @param {object} [options.limits] the server's (serverLimits.js): the batches stay under its archive size and file count
 * @param {(state: {phase: string, results: Object<string, object>, batch: number, batches: number, sentBytes: number, totalBytes: number}) => void} [options.onProgress]
 *   sentBytes / totalBytes: the files' bytes in the parts the server answered, of all (the bar of a long upload)
 * @returns {Promise<{results: Object<string, {status: string, path?: string, error?: string}>, bytesByPath: Map, cancelled: boolean, serverMayHaveSaved: boolean}>}
 *   results keyed by relPath; `path` is where the server put the file, relative to vfsTarget.
 */
export async function runUpload({ projectId, vfsTarget, files, conflict, signal, onProgress, limits = SERVER_LIMITS }) {
  const results = Object.fromEntries(files.map((file) => [file.relPath, { status: 'queued' }]));
  const allBytes = new Map();
  const batches = batchFiles(files, batchOptions(limits));
  const totalBytes = files.reduce((sum, file) => sum + (file.size || 0), 0);
  let sentBytes = 0;
  const report = (phase, batch) => onProgress?.({ phase, results: { ...results }, batch, batches: batches.length, sentBytes, totalBytes });
  let cancelled = false;
  let serverMayHaveSaved = false;
  const target = joinPath(vfsTarget);
  const prefix = target ? `${target}/` : '';

  for (let batchIndex = 0; batchIndex < batches.length; batchIndex += 1) {
    const batch = batches[batchIndex];
    if (signal?.aborted) { cancelled = true; break; }
    try {
      for (const file of batch) { results[file.relPath] = { status: 'packing' }; }
      report('packing', batchIndex);
      // eslint-disable-next-line no-await-in-loop
      const { archive, bytesByPath } = await packZip(batch, {
        signal,
        onFile: (index) => {
          results[batch[index].relPath] = { status: 'packed' };
          report('packing', batchIndex);
        },
      });
      bytesByPath.forEach((bytes, relPath) => allBytes.set(relPath, bytes));
      for (const file of batch) { results[file.relPath] = { status: 'sending' }; }
      report('sending', batchIndex);
      // eslint-disable-next-line no-await-in-loop
      const answer = await vfsClient.uploadArchive(projectId, { targetPath: target || '.', archive, conflict, signal });
      const byRequested = new Map((answer.files || []).map((line) => [line.requested || line.path, line]));
      for (const file of batch) {
        const line = byRequested.get(`${prefix}${file.relPath}`);
        results[file.relPath] = line
          ? { status: line.status, path: line.path.startsWith(prefix) ? line.path.slice(prefix.length) : line.path, ...(line.reason ? { reason: line.reason } : {}), ...(line.error ? { error: line.error } : {}) }
          : { status: 'failed', error: 'The server did not report this file' };
      }
      sentBytes += batch.reduce((sum, file) => sum + (file.size || 0), 0);
      report('sent', batchIndex);
    } catch (error) {
      const aborted = error instanceof Cancelled || error?.name === 'AbortError' || signal?.aborted;
      if (aborted) {
        cancelled = true;
        // Aborted while the archive was on its way: the server may have taken it anyway.
        if (batch.some((file) => results[file.relPath].status === 'sending')) { serverMayHaveSaved = true; }
        for (const file of batch) { results[file.relPath] = { status: 'cancelled' }; }
        break;
      }
      for (const file of batch) { results[file.relPath] = { status: 'failed', error: error?.message || 'Upload failed', ...(error?.status ? { httpStatus: error.status } : {}) }; }
      report('sent', batchIndex);
    }
  }
  if (cancelled) {
    for (const file of files) {
      if (['queued', 'packing', 'packed', 'sending'].includes(results[file.relPath].status)) { results[file.relPath] = { status: 'cancelled' }; }
    }
  }
  report(cancelled ? 'cancelled' : 'done', batches.length - 1);
  return { results, bytesByPath: allBytes, cancelled, serverMayHaveSaved };
}
